import {getPriceInCurrency} from '../../common/locales';
import {BILLING_TYPE_TARIFF, BILLING_TYPE_FREE, BILLING_TYPE_INSTANCE, BILLING_TYPE_INSTANCE_INVOCATION, BILLING_TYPE_INVOCATION} from '../../common/model-api/FlowDefType';
import {getMyCurrency} from '../basicLocales';
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import DetailToolBar from '../fw/DetailToolBar.react.js';
import DetailBox from '../fw/DetailBox.react.js';
import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import ChannelIcon from '../channel-defs/ChannelIcon.react';
import * as Tool from '../fw/Tool';
import {sortRules} from '../../common/model-api/ScenarioChannelDefRuleType';
import LoadingButton from '../fw/LoadingButton.react';
import ScenarioIcon from './ScenarioIcon.react';
import {variableResolver} from '../../common/util/VariableResolver';

import "./Scenario.scss";
import {getLastOrganisationId} from '../app/LastOrganisationId';
import Link from 'react-router/lib/Link';

import {FormattedMessage, injectIntl, defineMessages, IntlShape} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import ScreenDisplayer, {ScreenDisplayerListener} from '../view/box-utils/ScreenDisplayer.react';
import {getViewDefinitionFragment, getDataSourceDefinitionFragment} from '../view/box-utils/ScreenFragments.react';
import ScenarioDataSourceList from './ScenarioDataSourceList.react';
import FakePanel from './FakePanel.react';
import config from '../../common/config';
import * as Permission from "../../common/util/Permission";
import * as BrowserPermission from "../BrowserPermission";
import AgentLink from '../fw/AgentLink.react';
import {getUISettings} from '../app/UISettings.react';
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'ScenarioView.pageTitle', 'defaultMessage': '{appName} - integration with web applications with {productName}'},
    excerpt: {id: 'ScenarioView.excerpt', 'defaultMessage': '{appName} / {description} / {longDescription}'},
});

interface ScenarioViewProps {
    intl: IntlShape;
    relay: object;
    scenarioUrlFragment?: string;
    user: User;
}

class ScenarioView extends React.Component<ScenarioViewProps> {

    typeName = 'scenarios';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object.isRequired,
		scenarioUrlFragment: PropTypes.string,
        user: PropTypes.object.isRequired
    };

    constructor() {
        super();
    }

    static contextTypes = {
        router: PropTypes.object
    };

	// eslint-disable-next-line max-params
    renderScreenDef(screenDefs, channelDefMap, destinationChannelDef, destinationSourceDef) {
        return screenDefs.map(({node: data}) => {

            if (data.hideInScenario) {
                return null;
            }

            let views = data.views;
            let dataSources = data.dataSources;

            /* collect used channelDefs */
            let usedChannelsInViews = {};
            for (let view of views) {
                if (view['dataSource_id']) {
                    let dataSource = dataSources.find((dataSource) => dataSource['realId'] === view['dataSource_id']);
                    let channelDef = channelDefMap[dataSource['channelDefKey']];
                    if (channelDef) {
                        usedChannelsInViews[channelDef['realId']] = true;
                    }
                }

                if (view['connectedChannelDefKey']) {
                    let channelDef = view['connectedChannelDefKey'];
                    if (channelDef) {
                        usedChannelsInViews[channelDef['realId']] = true;
                    }
                }
            }

            let usedProductNames = Object.keys(usedChannelsInViews).map((channelDefId, idx) => {
                let channelDef = Object.values(channelDefMap).find((channelDef) => channelDef['realId'] === channelDefId);
                if (channelDef) {
                    return channelDef.name;
                }
            }).filter((a) => a ? true : false);

            return (
                <div key={data.realId}>

                    <FakePanel channelDefType={destinationChannelDef ? destinationChannelDef.type : null} sourceDefType={destinationSourceDef ? destinationSourceDef.type : null}>
                        <ScreenDisplayer
                            content={data.content}
                            isPreview
                            listener={new ScreenDisplayerListener()}
                            relay={this.props.relay}
                            viewsWithData={Object.keys(views).map((viewId) => {
                                let view = views[viewId];
                                let dataSource;

                                if (view['dataSource_id']) {
                                    dataSource = dataSources.find((dataSource) => dataSource['realId'] === view['dataSource_id']);
                                }

                                let result = ({
                                    view: view,
                                    data: '[]',
                                    entityDef: dataSource ? dataSource['primaryEntityDef'] : null,
                                    addon: '{}',
                                });

                                return result;
                            })}
                        />
                    </FakePanel>

                    <div className="scenario-note"><FormattedMessage defaultMessage="Example showing {productName} in {destinationChannelDefName} {productLength, plural, =0 {.} one {with component from {lastProduct}.} other {with components from {productsWithoutLast} and {lastProduct}.}} You can add more components from other scenarios aswell."
                                           id="scenario.usedApplications"
                                           values={{
                                               productName: config.PRODUCT_NAME,
                                               destinationChannelDefName: destinationChannelDef ? destinationChannelDef.name : '?',
                                               productLength: usedProductNames.length,
                                               productsAll: usedProductNames.join(", "),
                                               productsWithoutLast: usedProductNames.filter((e, idx) => idx < usedProductNames.length - 1).join(", "),
                                               lastProduct: usedProductNames[usedProductNames.length - 1],
                                           }}
                    /></div>

                    <div>
                        <h4><FormattedMessage defaultMessage="How are data collected" id="scenario.dataCollection" /></h4>
                        <ScenarioDataSourceList channelDefMap={channelDefMap} dataSources={dataSources} destinationChannelDef={destinationChannelDef} screen={data} />
                    </div>
                </div>
            );
        });
    }

    onApplyScenarioClick = () => {
        if (!this.props.user.organisation) {
            return null;
        }

        const scenario = this.props.user.scenarios.edges[0].node;

        let path = urlMapping.getUrl(types['channels'], 'scenario-apply', {
            organisation_id: this.props.user.organisation.realId,
            urlFragment: scenario.urlFragment,
            scenario_id: scenario.realId,
        });

        this.context.router.push(path);
    };

	goToApplication(channelDef) {
		let path = urlMapping.getUrl('channelDefs', 'public-apps', channelDef);
		this.context.router.push(path);
	}

    render() {
        const scenario = this.props.user.scenarios.edges[0].node;
        const metadata = types[this.typeName];
        let type = types[this.typeName];
        let title = Tool.getDataForColumn('title', metadata.fields(), scenario);
        if (!title) {
            title = Tool.getDataForColumn('name', metadata.fields(), scenario);
        }
        let description = Tool.getDataForColumn('description', metadata.fields(), scenario);
        let longDescription = Tool.getFormatted(metadata, 'longDescription', this.props.intl.formatMessage, scenario);

        let pageTitle = this.props.intl.formatMessage(messages.title, {appName: title, productName: config.PRODUCT_NAME});
        let excerpt = this.props.intl.formatMessage(messages.excerpt, {appName: title, description: description, longDescription: Tool.getDataForColumn('longDescription', metadata.fields(), scenario)});

        let scenarios2ChannelDefs = scenario.scenarios2ChannelDefs.edges.map(({node}) => node).sort(sortRules);

        let icons = [];

        let destinationChannelDef = null;
        let channelDefMap = {};

        let idx = 0;
        for (let scenarios2ChannelDef of scenarios2ChannelDefs) {
            channelDefMap[scenarios2ChannelDef.key] = scenarios2ChannelDef.channelDef;

            if (scenarios2ChannelDef.ruleType === 'destination') {
                destinationChannelDef = scenarios2ChannelDef.channelDef;
            }

            if (idx > 0) {
                icons.push(<span className="scenarioGridList-plus" key={idx + "-plus"}>+</span>);
            }
            icons.push(<ChannelIcon channelDef={scenarios2ChannelDef.channelDef} key={idx} size={128}/>);
            idx++;
        }

        let alreadyApplied = false;
        if (this.props.user.organisation && this.props.user.organisation.usedScenariosByURL && this.props.user.organisation.usedScenariosByURL.edges.length > 0) {
            alreadyApplied = true;
        }

        let privacy = Tool.getFormatted(metadata, 'privacy', this.props.intl.formatMessage, scenario);

        let requiredPayPlan = null;
        let showRequiredPayPlan = false;

        if ((!scenario.billingType || scenario.billingType === BILLING_TYPE_TARIFF)) {
            showRequiredPayPlan = true;
            if (scenario.requiredPayPlan && scenario.requiredPayPlan !== "-") {
                requiredPayPlan = Tool.getFormatted(metadata, 'requiredPayPlan', this.props.intl.formatMessage, scenario);
            }
        }

        let canAccessRequiredPayPlan = true;

        const appNo = scenario.scenarios2ChannelDefs.edges.filter(({node}) => !node.doNotShowToUser).length;
		let columnsCnt = appNo < 5 ? appNo : (appNo < 7 ? 3 : (appNo < 9 ? 4 : 3));

		let columnLg;
		let columnMd;
		let columnSm;
		switch (columnsCnt) {
			case 1:
				columnLg = 12;
				columnMd = 12;
				columnSm = 12;
				break;
			case 2:
				columnLg = 6;
				columnMd = 6;
				columnSm = 6;
				break;
			case 3:
				columnLg = 4;
				columnMd = 4;
				columnSm = 6;
				break;
			case 4:
			default:
				columnLg = 3;
				columnMd = 4;
				columnSm = 6;
				break;
		}

        let installationPrice1 = "";
        let installationPrice2 = "";

        if (scenario.priceInstallation && scenario.priceInstallation > 0) {
            installationPrice1 = (<li><FormattedMessage defaultMessage="Installation and configuration by a Dativery employee ({link}): {price}"
                              id="scenario.payPlan.installation-price3"
                              values={{price: this.props.intl.formatNumber(getPriceInCurrency(scenario.priceInstallation,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}), link: <a href="https://www.dativery.com/cs/podpora/jak-objednat/instalace-konzultantem/" target="_new"><FormattedMessage defaultMessage="more info" id="scenario.moreInfo" /></a>}}/></li>);

            if (!scenario.showContactUs) {
                installationPrice2 = (<li><FormattedMessage defaultMessage="Installation and configuration by the user ({link}): {price}"
                                id="scenario.payPlan.installation-price4"
                                values={{price: this.props.intl.formatNumber(getPriceInCurrency(scenario.priceSelfInstallation || 0,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}), link: <a href="https://www.dativery.com/cs/podpora/jak-objednat/instalace-uzivatelem/" target="_new"><FormattedMessage defaultMessage="more info" id="scenario.moreInfo" /></a>}}/></li>);
            }
        }

        let priceText = null;

        if (scenario.priceText) {
            priceText = (<ul><li>{variableResolver(scenario.priceText, {
                pricePerInstance: this.props.intl.formatNumber(getPriceInCurrency(scenario.pricePerInstance,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                pricePerInstanceNext: this.props.intl.formatNumber(getPriceInCurrency(scenario.pricePerInstanceNext,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                priceInstallation: this.props.intl.formatNumber(getPriceInCurrency(scenario.priceInstallation,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                priceInstallationNext: this.props.intl.formatNumber(getPriceInCurrency(scenario.priceInstallationNext,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                priceSelfInstallation: this.props.intl.formatNumber(getPriceInCurrency(scenario.priceSelfInstallation,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                pricePerInvocation: this.props.intl.formatNumber(getPriceInCurrency(scenario.pricePerInvocation,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                invocationName: scenario.invocationName,
            })}</li>{installationPrice1}{" "}{installationPrice2}</ul>);
        } else if (scenario.billingType === BILLING_TYPE_FREE) {
            priceText = <ul>{!installationPrice1 && !installationPrice2 ? <li><FormattedMessage defaultMessage="Free" id="scenario.billingType.free" /></li> : ""}{installationPrice1}{" "}{installationPrice2}</ul>;
        } else if (scenario.billingType === BILLING_TYPE_TARIFF) {
            priceText = <ul><li><FormattedMessage defaultMessage="Part of tariff" id="scenario.billingType.tariff" /></li>{" "}{installationPrice1}{" "}{installationPrice2}</ul>;
        } else if (scenario.billingType === BILLING_TYPE_INSTANCE || scenario.billingType === BILLING_TYPE_INSTANCE_INVOCATION) {
            if (scenario.pricePerInstance === scenario.pricePerInstanceNext || scenario.pricePerInstanceNext === undefined || scenario.pricePerInstanceNext === 0 || scenario.pricePerInstanceNext === null || scenario.pricePerInstanceNext === "0.00") {
                priceText = (<ul><li><FormattedMessage defaultMessage="Price {price} / month" id="scenario.billingType.instanceSame" values={{
                    price: this.props.intl.formatNumber(getPriceInCurrency(scenario.pricePerInstance,      getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0})}}/></li>{" "}{installationPrice1}{" "}{installationPrice2}</ul>);
            } else {
                priceText = (<ul><li><FormattedMessage defaultMessage="Price {price1} / month (another {price2} / month)" id="scenario.billingType.instanceDifferent" values={{
                    price1: this.props.intl.formatNumber(getPriceInCurrency(scenario.pricePerInstance,     getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                    price2: this.props.intl.formatNumber(getPriceInCurrency(scenario.pricePerInstanceNext, getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0})}}/></li>{" "}{installationPrice1}{" "}{installationPrice2}</ul>);
            }
        } else if (scenario.billingType === BILLING_TYPE_INSTANCE_INVOCATION) {
            priceText = (<ul><li><FormattedMessage defaultMessage="You will be also billed {price} per {invocationName}" id="scenario.billingType.invocation2" values={{
                    price: this.props.intl.formatNumber(getPriceInCurrency(scenario.pricePerInvocation,    getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                    invocationName: scenario.invocationName}}/></li></ul>);
        } else if (scenario.billingType === BILLING_TYPE_INVOCATION) {
            priceText = (<ul><li><FormattedMessage defaultMessage="Price from {price} per {invocationName}" id="scenario.billingType.invocation" values={{
                    price: this.props.intl.formatNumber(getPriceInCurrency(scenario.pricePerInvocation,    getMyCurrency()), {style: 'currency', currency: getMyCurrency(), minimumFractionDigits: 0}),
                    invocationName: scenario.invocationName}}/></li>{" "}{installationPrice1}{" "}{installationPrice2}</ul>);
        }

        return (
            <AppPanel breadcrumbParams={[
                {type, data: scenario},
                {type: types['scenarios'], data: scenario}
            ]} className="scenarioWizzardNew" title={pageTitle}>
                <div className="scenarioView"
                     data-description={scenario.description}
                     data-excerpt={excerpt}
                     data-title={pageTitle}
                >
                    <DetailBox data={scenario} metadata={type} urlMapping={urlMapping}>
                        {BrowserPermission.hasPermission(Permission.PERM_DEVELOPER) && BrowserPermission.developerModeEnabled() ?
                        <DetailToolBar data={scenario} metadata={type} urlMapping={urlMapping}>
                            <Link className="btn btn-default" to={urlMapping.getUrl(types['flows'], 'scenarios-list', scenario)}><i aria-hidden="true" className="fa fa-fw fa-history"></i> <FormattedMessage defaultMessage="Flows" id="scenarios.flows" /></Link>
                            <Link className="btn btn-default" to={urlMapping.getUrl(types['organisations'], 'scenarios-list', scenario)}><i aria-hidden="true" className="fa fa-fw fa-history"></i> <FormattedMessage defaultMessage="Organisations" id="scenarios.organisations" /></Link>
                        </DetailToolBar>
                        : null}

						<div className="header">
                        <div dangerouslySetInnerHTML={{ __html: "<!-- SCENARIO_ICON:BEGIN -->" }} /><ScenarioIcon scenario={scenario} /><div dangerouslySetInnerHTML={{ __html: "<!-- SCENARIO_ICON:END -->" }} />

							<div className="text">
								<span className="flex"></span>
								<h2>{scenario.title}</h2>
                                <p className="shortDescription">{scenario.description}</p>

                                {requiredPayPlan ?
                                    <div className="scenario-note">
                                        {requiredPayPlan || scenario.extraPayment ?
                                            <span>{requiredPayPlan ?
                                                (scenario.extraPayment ?
                                                        <FormattedMessage defaultMessage="Requires {planName} and extra payment for some service."
                                                                          id="scenario.payPlan.requires-and-payment"
                                                                          values={{planName: requiredPayPlan}}/>
                                                        :
                                                        <FormattedMessage defaultMessage="Requires {planName}."
                                                                          id="scenario.payPlan.requires"
                                                                          values={{planName: requiredPayPlan}}/>
                                                ) :
                                                <FormattedMessage defaultMessage="Requires extra payment for some service."
                                                                  id="scenario.payPlan.requires-extra-payment"
                                                                  values={{planName: requiredPayPlan}}/>
                                            }<br/></span> : null
                                        }
                                    </div> : null
                                }
                                {priceText ? <div className="scenarioPricing"><div dangerouslySetInnerHTML={{ __html: "<!-- SCENARIO_PRICE:BEGIN -->" }} />{priceText}<div dangerouslySetInnerHTML={{ __html: "<!-- SCENARIO_PRICE:END -->" }} /></div> : null}
								<DetailToolBar data={scenario} metadata={type} urlMapping={urlMapping}>
                                    {(scenario.showContactUs && (!BrowserPermission.hasPermission(Permission.PERM_EDIT_CHANNEL_DEF) || !BrowserPermission.developerModeEnabled()) ?
                                        <div className="dativery-scenario-apply-button"><Link className="btn btn-primary" href={"mailto:" + config.SALES_EMAIL + '?subject=' + encodeURIComponent(scenario.title)}><FormattedMessage
                                        defaultMessage="Contact Us"
                                        id="scenarios.contactUs"/></Link></div>
                                    :
                                    (this.props.user.organisation ? (
                                            BrowserPermission.hasOrganisationPermission(Permission.PERM_EDIT_CHANNEL, this.props.user.organisation.realId) ?
                                        (canAccessRequiredPayPlan ?
                                            <LoadingButton className="btn btn-primary"
                                                            onClick={this.onApplyScenarioClick}><FormattedMessage
                                                defaultMessage="Apply scenario"
                                                disabled={this.props.user.organisation ? false : true}
                                                id="scenarios.applyScenario"/></LoadingButton>
                                            :
                                                <button className="btn btn-default" disabled><FormattedMessage defaultMessage="Requires {planName}."
                                                                                                               id="scenario.payPlan.requires"
                                                                                                               values={{planName: requiredPayPlan}}/></button>
                                        ) :
                                            <button className="btn btn-default" disabled><FormattedMessage defaultMessage="You don't have enough permission to apply scenario."
                                                                                                           id="scenario.permission.requires" /></button>
                                        )
                                        : <div className="dativery-scenario-apply-button"><Link className="btn btn-primary" href={config.PUBLIC_APP_URL + urlMapping.getUrl(types['scenarios'], 'scenario-apply-pre', {urlFragment: scenario.urlFragment})} to={urlMapping.getUrl(types['scenarios'], 'scenario-apply-pre', {urlFragment: scenario.urlFragment})}><FormattedMessage
                                            defaultMessage="Apply scenario"
                                            id="scenarios.applyScenario"/></Link></div>
                                    ))}
								</DetailToolBar>
								<span className="flex"></span>
							</div>
						</div>
                    </DetailBox>

                    <div dangerouslySetInnerHTML={{ __html: "<!-- SCENARIO_DESCRIPTION:BEGIN -->" }} />
					{privacy ?
						<div className="scenario-note big">
							<span><strong>{Tool.getLabel(types['scenarios'], 'privacy', this.props.intl.formatMessage)}:</strong> {privacy}<br/></span>
						</div>
					: null}

					<div className="longDescription">{longDescription}</div>

                    {(scenario.tutorialLink || scenario.documentationLink || scenario.techDocumentationLink) ? <div className="longDescription">
                        <ul>
                            {scenario.tutorialLink ? <li><a href={scenario.tutorialLink} target="_blank"><FormattedMessage defaultMessage="Read tutorial…" id="scenarioView.tutorialLink" /></a></li> : null}
                            {scenario.documentationLink ? <li><a href={scenario.documentationLink} target="_blank"><FormattedMessage defaultMessage="More documentation…" id="scenarioView.documentationLink" /></a></li> : null}
                            {scenario.techDocumentationLink ? <li><a href={scenario.techDocumentationLink} target="_blank"><FormattedMessage defaultMessage="Technical documentation…" id="scenarioView.techDocumentationLink" /></a></li> : null}
                        </ul>
                    </div> : null}
                    <div dangerouslySetInnerHTML={{ __html: "<!-- SCENARIO_DESCRIPTION:END -->" }} />

                    <h3><FormattedMessage defaultMessage="Connected Applications and Services" id="scenario.connectedApps.title" /></h3>
                    <div className="scenario-apps-list">
                        {scenario.scenarios2ChannelDefs.edges.map(({node}) => node).sort(sortRules).map((scenarios2ChannelDef, idx, arr) => {
                            if (!scenarios2ChannelDef.channelDef) {
                                return null;
                            }
                            if (scenarios2ChannelDef.doNotShowToUser) {
                                return null;
                            }

                            let connectRequirement = null;
                            if (scenarios2ChannelDef.channelDef.connectRequirement) {
                                connectRequirement = Tool.getFormatted(types['channelDefs'], 'connectRequirement', this.props.intl.formatMessage, scenarios2ChannelDef.channelDef);
                            }
                            if (scenarios2ChannelDef.channelDef.requiresAgent) {
                                connectRequirement = (<span><FormattedMessage defaultMessage="Requires installation of {agentLink}." id="channelDef.requiresAgentUrl" values={{
                                    productName: config.PRODUCT_NAME,
                                    agentLink: <AgentLink />
                                }} /> {connectRequirement}</span>);
                            }

                            let myRequiredPayPlan =
                                scenarios2ChannelDef.ruleType === 'destination' ?
                                    scenarios2ChannelDef.channelDef.addonRequiredPayPlan : scenarios2ChannelDef.channelDef.requiredPayPlan;

                            if (myRequiredPayPlan) {
                                if (myRequiredPayPlan === "-") {
                                    myRequiredPayPlan = null;
                                } else {
                                    myRequiredPayPlan = Tool.getFormatted(types['channelDefs'], 'requiredPayPlan', this.props.intl.formatMessage, scenarios2ChannelDef.channelDef);
                                }
                            }

                            /* if whole scenario doesn't need payPlan, do not show scenario for this application */
                            if (!showRequiredPayPlan) {
                                myRequiredPayPlan = null;
                            }

                            let requiresCaching = scenarios2ChannelDef.requiresCaching;
                            let requiresWritePermission = scenarios2ChannelDef.requiresWritePermission;

                            if (getUISettings().isUpgatesAddon) {
                                return (
                                    <div className={"scenario-apps-row col-sm-" + columnSm + " col-md-" + columnMd + " col-lg-" + columnLg} key={idx} role="link">
                                        <div className="scenario-apps-logo"><ChannelIcon channelDef={scenarios2ChannelDef.channelDef} size={64}/></div>
                                        <div className="scenario-apps-text"><h4>{scenarios2ChannelDef.channelDef.name}</h4>
                                            <p>{scenarios2ChannelDef.channelDef.description}</p>
                                            <div className="scenario-note"><strong>{Tool.getLabel(types['channelDefs'], 'privacy', this.props.intl.formatMessage)}:</strong> {Tool.getFormatted(types['channelDefs'], 'privacy', this.props.intl.formatMessage, scenarios2ChannelDef.channelDef)}<br/>
                                                {connectRequirement ? <span><strong>{Tool.getLabel(types['channelDefs'], 'connectRequirement', this.props.intl.formatMessage)}:</strong> {connectRequirement}<br/></span> : null}
                                                {myRequiredPayPlan ? <span><strong>{Tool.getLabel(types['channelDefs'], 'requiredPayPlan', this.props.intl.formatMessage)}:</strong> {myRequiredPayPlan}<br/></span> : null}
                                                {requiresCaching ? <span><strong><FormattedMessage defaultMessage="Scenario requires enabled caching of data in {productName}." id="scenario.connectedApps.requiresCaching" values={{productName: config.PRODUCT_NAME}}/></strong><br/></span> : null}
                                                {requiresWritePermission ? <span><strong><FormattedMessage defaultMessage="Scenario requires write permission." id="scenario.connectedApps.requiresWritePermission" values={{productName: config.PRODUCT_NAME}}/></strong><br/></span> : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            return (
								<div className={"scenario-apps-row col-sm-" + columnSm + " col-md-" + columnMd + " col-lg-" + columnLg} key={idx} role="link">
									<div className="applicationLink" onClick={() => {this.goToApplication(scenarios2ChannelDef.channelDef);}}>
                                        <Link href={urlMapping.getUrl('channelDefs', 'public-apps', scenarios2ChannelDef.channelDef)}>
                                        <div className="scenario-apps-logo"><ChannelIcon channelDef={scenarios2ChannelDef.channelDef} size={64}/></div>
                                        <div className="scenario-apps-text"><h4>{scenarios2ChannelDef.channelDef.name}</h4>
                                            <p>{scenarios2ChannelDef.channelDef.description}</p>
                                            <div className="scenario-note"><strong>{Tool.getLabel(types['channelDefs'], 'privacy', this.props.intl.formatMessage)}:</strong> {Tool.getFormatted(types['channelDefs'], 'privacy', this.props.intl.formatMessage, scenarios2ChannelDef.channelDef)}<br/>
                                                {connectRequirement ? <span><strong>{Tool.getLabel(types['channelDefs'], 'connectRequirement', this.props.intl.formatMessage)}:</strong> {connectRequirement}<br/></span> : null}
                                                {myRequiredPayPlan ? <span><strong>{Tool.getLabel(types['channelDefs'], 'requiredPayPlan', this.props.intl.formatMessage)}:</strong> {myRequiredPayPlan}<br/></span> : null}
                                                {requiresCaching ? <span><strong><FormattedMessage defaultMessage="Scenario requires enabled caching of data in {productName}." id="scenario.connectedApps.requiresCaching" values={{productName: config.PRODUCT_NAME}}/></strong><br/></span> : null}
                                                {requiresWritePermission ? <span><strong><FormattedMessage defaultMessage="Scenario requires write permission." id="scenario.connectedApps.requiresWritePermission" values={{productName: config.PRODUCT_NAME}}/></strong><br/></span> : null}
                                            </div>
                                        </div>
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
						<div className="clear" />
					</div>

                    {scenario.scenarioDef && scenario.scenarioDef.screenDefs && scenario.scenarioDef.screenDefs.edges && scenario.scenarioDef.screenDefs.edges.length > 0 ?
                        <div>
                            <h3><FormattedMessage defaultMessage="Screen preview" id="scenario.screenPreview" /></h3>

                            {this.renderScreenDef(scenario.scenarioDef.screenDefs.edges, channelDefMap, destinationChannelDef, null)}
                        </div>
                        : null}

                    {scenario.scenarioDef && !getUISettings().isUpgatesAddon ?
                        <div className="channelDef-bottom">
                            <FormattedMessage defaultMessage="Scenario Type" id="scenario.scenarioType" />: <Link href={"/s-type/" + scenario.scenarioDef.urlFragmentRaw + "/"} to={"/s-type/" + scenario.scenarioDef.urlFragmentRaw + "/"}>{scenario.scenarioDef.name}</Link><br/>
                            {scenario.developerGroup ? <span><FormattedMessage defaultMessage="Developer" id="scenario.dveloperGroup" />: {scenario.developerGroup.name}</span> : null}
                        </div>
                    : null}
                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(ScenarioView), (props) => {
    return props.user.scenarios.edges.length > 0;
}), {
    initialVariables: {
        organisationId: '',
        scenarioUrlFragment: '',
        organisationSelected: false,
    },
    prepareVariables (variables) {
        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        variables['organisationSelected'] = variables['organisationId'] ? true : false;

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
				featureFlags,
                organisation(id: $organisationId) @include(if: $organisationSelected) {
                    id,
                    realId,
                    name,
                    featureFlags,
                    payPlan,
                    usedScenariosByURL(urlFragment: $scenarioUrlFragment, first: 10) {
                        edges {
                            node {
                                id,
                                realId,
                                scenario_id,
                                scenarioDef_id,
                            }
                        }
                    },
                    channels(first: 1000, deleted: false) {
                        edges {
                            node {
                                name,
                                type,
                                channelDef {
                                    id,
                                    realId,
                                    icon,
                                    name,
                                    state,
                                    extendedFrom {
                                        id,
                                        realId,
                                        icon
                                    },
                                },
                                id,
                                organisation_id,
                            }
                        },
                    },
                },
                scenarios: publicScenarios(first: 1, urlFragment: $scenarioUrlFragment) {
                    edges {
                        node {
                            id,
                            name,
                            title,
                            description,
                            longDescription,
                            state,
                            channelState,
                            visibility,
                            realId,
                            privacy,
                            extraPayment,
                            billingType,
                            pricePerInvocation,
                            pricePerInstance,
                            pricePerInstanceNext,
                            priceText,
                            priceSelfInstallation,
                            priceInstallation,
                            priceInstallationNext,
                            invocationName,
                            requiredPayPlan,
                            urlFragment,
                            showContactUs,
                            developerGroup {
                                id,
                                name,
                            },
                            tutorialLink,
                            documentationLink,
                            techDocumentationLink,   

							${ScenarioIcon.getFragment('scenario')}

                            scenarioDef {
                                name,
                                description,
                                longDescription,
                                urlFragmentRaw,
                                realId,
                                screenDefs(first: 10) {
                                    edges {
                                        node {
                                            id,
                                            realId,
                                            name,
                                            description,
                                            hideInScenario,
                                            entityDef_id,
                                            entityDef {
                                                id,
                                                name,
                                            },
                                            scenarioDef_id,
                                            content,
                                            operation,
                                            views {
                                                ${getViewDefinitionFragment()}
                                            },
                                            dataSources {
                                                ${getDataSourceDefinitionFragment()}
                                            }
                                        }
                                    }
                                },
                            },
                            scenarios2ChannelDefs(first: 100) {
                                edges {
                                    node {
                                        id,
                                        realId,
                                        key,
                                        ruleType,
                                        requiresCaching,
                                        requiresWritePermission,
                                        doNotShowToUser,
                                        order,
                                        channelDef {
                                            id,
                                            realId,
                                            name,
                                            icon,
                                            type,
                                            description,
                                            requiresAgent,
                                            privacy,
                                            connectRequirement,
                                            extraPayment,
                                            requiredPayPlan,
                                            addonRequiredPayPlan,
                                            urlFragment,
                                            extendedFrom {
                                                id,
                                                realId,
                                                icon
                                            },
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
});
